import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { ChevronDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useCreateFolder } from '@/features/manage-project-folders/api/useCreateFolder.mutation';
import { useWorkspaceTagSuggestions } from '@/entities/project-tag';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useUpdateFolder } from '@/features/manage-project-folders/api/useUpdateFolder.mutation';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type { CreateFolderModalProps } from './CreateFolderModal.types';
import { LinkedBriefField } from './LinkedBriefField';
import { buildFolderFormSchema, type FolderFormData } from './folder-form.schema';
import { useTranslation } from 'react-i18next';

/** Wires the visible "Linked brief" label to the Select trigger it labels. */
const BRIEF_TRIGGER_ID = 'create-folder-modal-brief-trigger';

export function CreateFolderModal(props: CreateFolderModalProps) {
  const { t } = useTranslation('projects');

  const navigate = useNavigate();
  const isEdit = props.mode === 'edit';
  const editFolder = isEdit ? props.folder : null;
  const parentId = isEdit ? undefined : props.parentId;
  const isSubfolder = !isEdit && !!parentId;

  // A subfolder inherits the parent's brief instead of carrying one, so the
  // field is absent there rather than present and refused on submit.
  const showBriefField = !isSubfolder && !editFolder?.parentId;

  const workspaceId = useCurrentWorkspaceId();
  const { suggestions } = useWorkspaceTagSuggestions(workspaceId);

  const defaultValues = useMemo<FolderFormData>(
    () => ({
      name: editFolder?.name ?? '',
      briefId: editFolder?.brief?.id ?? null,
      tags: editFolder?.tags.map((tag) => tag.name) ?? [],
    }),
    [editFolder],
  );

  const formSchema = useMemo(() => buildFolderFormSchema(t), [t]);

  const form = useForm<FolderFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    // Submit-time rather than `onTouched`: the dialog autofocuses the name
    // field, so a blur-time check fires the moment the user reaches for the
    // close button, and the message it renders grows the centred dialog enough
    // to slide that 16px button out from under the pointer — swallowing the
    // very click that asked to close. The CTA already stays disabled while the
    // name is empty, so the blur check told the user nothing they could act on.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Reset the form whenever the modal re-opens with different defaults so
  // edit-mode picks up the freshest folder snapshot every time it opens.
  useEffect(() => {
    if (props.open) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, props.open]);

  const tagsAnchorRef = useRef<HTMLDivElement | null>(null);

  const createMutation = useCreateFolder({ projectId: props.platformProjectId });
  const updateMutation = useUpdateFolder({ projectId: props.platformProjectId });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const watchedName = form.watch('name');
  const canSubmit = !isPending && watchedName.trim().length > 0;

  const handleMutationError = (error: unknown) => {
    const fallback = isEdit
      ? t('manageProjectFolders.createFolderModal.failedToUpdateFolder')
      : t('manageProjectFolders.createFolderModal.failedToCreateFolder');
    const { status, detail } = parseProblemDetail(error, fallback);
    if (status === 409) {
      form.setError('name', { type: 'server', message: detail });
      return;
    }
    notificationService.showError(detail);
  };

  const titleText = isEdit
    ? t('manageProjectFolders.createFolderModal.editFolder')
    : isSubfolder
      ? t('manageProjectFolders.createFolderModal.createNewSubfolder')
      : t('manageProjectFolders.createFolderModal.createNewFolder');
  const ctaText = isEdit
    ? t('manageProjectFolders.createFolderModal.save')
    : isSubfolder
      ? t('manageProjectFolders.createFolderModal.createSubfolder')
      : t('manageProjectFolders.createFolderModal.createFolder');

  // `onClose` alone — the host unmounts on its own timer. See `useModalState`.
  const handleClose = () => {
    if (isPending) return;
    props.onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const onSubmit = form.handleSubmit((formData) => {
    if (editFolder) {
      updateMutation.mutate(
        {
          folderId: editFolder.id,
          data: {
            name: formData.name,
            tags: formData.tags,
            ...(showBriefField && { briefId: formData.briefId }),
          },
        },
        {
          onSuccess: (folder) => {
            props.onSuccess?.(folder);
            handleClose();
          },
          onError: handleMutationError,
        },
      );
    } else {
      createMutation.mutate(
        {
          data: {
            name: formData.name,
            parentId,
            tags: formData.tags,
            ...(showBriefField && formData.briefId ? { briefId: formData.briefId } : {}),
          },
        },
        {
          onSuccess: (folder) => {
            if (props.onSuccess) {
              props.onSuccess(folder);
            } else {
              void navigate(ROUTES.PROJECT_FOLDER_DETAIL(props.platformProjectId, folder.id));
            }
            handleClose();
          },
          onError: handleMutationError,
        },
      );
    }
  });

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col"
          >
            <DialogBody className="flex flex-col gap-4 py-10">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('manageProjectFolders.createFolderModal.folderName')}
                      <span aria-hidden="true" className="text-destructive">
                        *
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('manageProjectFolders.createFolderModal.enterTheFolderName')}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => {
                  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== 'Enter') return;
                    const target = event.currentTarget;
                    const text = target.value.trim();
                    if (text === '') return;
                    if (field.value.includes(text)) {
                      target.value = '';
                      return;
                    }
                    event.preventDefault();
                    field.onChange([...field.value, text]);
                    target.value = '';
                  };

                  return (
                    <FormItem>
                      <FormLabel>{t('manageProjectFolders.createFolderModal.tags')}</FormLabel>
                      <FormControl>
                        <Combobox<string, true>
                          multiple
                          value={field.value}
                          onValueChange={field.onChange}
                          items={suggestions}
                          disabled={isPending}
                          itemToStringLabel={(item) => item}
                          itemToStringValue={(item) => item}
                        >
                          <ComboboxChips ref={tagsAnchorRef}>
                            {field.value.map((tag, index) => (
                              <ComboboxChip
                                key={`${tag}:${index}`}
                                aria-label={t('manageProjectFolders.createFolderModal.removeTag', {
                                  tag,
                                })}
                                className="rounded-full bg-accent px-2.5 text-accent-foreground"
                              >
                                {tag}
                              </ComboboxChip>
                            ))}
                            <ComboboxChipsInput
                              placeholder={t('manageProjectFolders.createFolderModal.enterTags')}
                              disabled={isPending}
                              onKeyDown={handleKeyDown}
                            />
                            <ChevronDown
                              aria-hidden="true"
                              className="ml-auto size-4 shrink-0 text-foreground-subtle pointer-events-none"
                            />
                          </ComboboxChips>
                          <ComboboxContent anchor={tagsAnchorRef} className="pointer-events-auto">
                            <ComboboxList>
                              <ComboboxCollection>
                                {(item: string) => (
                                  <ComboboxItem key={item} value={item}>
                                    {item}
                                  </ComboboxItem>
                                )}
                              </ComboboxCollection>
                              <ComboboxEmpty>
                                {t('manageProjectFolders.createFolderModal.noResults')}
                              </ComboboxEmpty>
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {showBriefField && (
                <FormField
                  control={form.control}
                  name="briefId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor={BRIEF_TRIGGER_ID}>
                        {t('manageProjectFolders.createFolderModal.linkedBrief')}
                      </FormLabel>
                      <FormControl>
                        <LinkedBriefField
                          projectId={props.platformProjectId}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isPending}
                          currentBrief={editFolder?.brief}
                          triggerId={BRIEF_TRIGGER_ID}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                {t('manageProjectFolders.createFolderModal.cancel')}
              </Button>
              <Button type="submit" variant="default" disabled={!canSubmit}>
                {isPending && <Spinner />} {ctaText}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
